// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { FieldArray, getFormInitialValues, reduxForm } from 'redux-form';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/styles';
import { Avatar, Chip, ListItemText, Typography } from '@material-ui/core';
import {
    Cancel as CancelIcon,
    Check as CheckIcon,
    Search as SearchIcon,
} from '@material-ui/icons';
import type { $AxiosXHR } from 'axios';
import type { FieldArrayProps, FormProps } from 'redux-form';
import type { Connector } from 'react-redux';
import type { ContextRouter } from 'react-router';
import type { TFunction } from 'react-i18next';
import type { Group } from 'gigwalk/lib/api/groups/types';
import { client as gigwalk } from '../../../../../../api/createGigwalkClient';
import highlightMatch from '../../../../../../util/highlightMatch';
import Select from '../../../../../../components/Select';
import Step from '../../../../components/Step';
import entitySelector from '../../../../../../redux/entities/entitySelector';
import getValidationRule from '../../../../utils/getValidationRule';
import { selectors } from './duck';
import styles from './styles';
import type { State as RootState } from '../../../../../../redux/initialState';

type OwnProps = {
    ...ContextRouter,
    ...FormProps,
    classes: Object,
    t: TFunction,
};

type StateProps = {
    editMode: boolean,
    memberCount: number,
};

type Props = OwnProps & StateProps;

const preventFormSubmission = (event) => {
    event.preventDefault();
    return false;
};

const formatOptionLabel = (option: Object, meta: Object) => {
    const { label } = option;
    const primary = highlightMatch(label, meta.inputValue);

    return (
        <Fragment>
            <ListItemText primary={primary} />
            {meta.isSelected ? <CheckIcon /> : null}
        </Fragment>
    );
};

export class Groups extends Component<Props> {
    loadOptions = (inputValue: string): Promise<Object[]> => {
        const { match } = this.props;
        const { orgId } = match.params;
        const params = {
            q: `*${inputValue}*`,
            limit: 20,
        };

        return gigwalk.client.get(`/v1/organizations/${orgId}/search/groups`, { params })
            .then((resp: $AxiosXHR<Object>) => {
                const searchResults = resp.data.hits ? resp.data.hits.hits || [] : [];
                const groups = searchResults.map((record: { _source: Group }) => record._source);

                return groups.map((group: Group) => ({
                    label: group.name,
                    value: group,
                }));
            });
    };

    handleGroupsChange = (value: Object[]) => {
        const { change } = this.props;
        change('groups', value.map((option: Object) => option.value));
    };

    handleRemoveGroup = (event: SyntheticMouseEvent<any>) => {
        const { index } = event.currentTarget.dataset;
        const { array } = this.props;
        array.remove('groups', index);
    };

    renderGroups = (props: FieldArrayProps): ?React$Element<*> => {
        const { classes, t } = this.props;
        const { fields } = props;
        const selected = [];

        const elements = fields.map((name: string, index: number) => {
            const group = fields.get(index);
            selected.push({ label: group.name, value: group });

            return (
                <Chip
                  avatar={<Avatar>{group.member_count}</Avatar>}
                  className={classes.group}
                  classes={{
                      root: classes.chipRoot,
                      avatar: classes.chipAvatar,
                      label: classes.chipLabel,
                      deleteIcon: classes.chipDeleteIcon,
                  }}
                  deleteIcon={<CancelIcon data-index={index} />}
                  key={name}
                  label={group.name}
                  onDelete={this.handleRemoveGroup}
                />
            );
        });

        return (
            <div>
                {elements.length > 0
                    ? (
                        <div className={classes.invitedGroups}>
                            <Typography className={classes.label} align="center">
                                {t('projectBuilder.people.groups.invitedGroups')}
                            </Typography>
                            <ul>{elements}</ul>
                        </div>
                    )
                    : null
                }
                <Select
                  backspaceRemovesValue={false}
                  classes={{
                      input: classes.selectInput,
                      placeholder: classes.selectPlaceholder,
                      root: classes.select,
                  }}
                  ControlProps={{ fullWidth: true }}
                  controlShouldRenderValue={false}
                  defaultOptions
                  disableSelectedOptions
                  dropdownIcon={<SearchIcon />}
                  formatOptionLabel={formatOptionLabel}
                  isClearable={false}
                  isMulti
                  loadOptions={this.loadOptions}
                  onChange={this.handleGroupsChange}
                  placeholder={t('projectBuilder.people.groups.addGroup')}
                  value={selected}
                />
            </div>
        );
    };

    render() {
        const { classes, memberCount, t } = this.props;

        // In some browsers, hitting the 'Enter' key in a text input that is the only field in the form will cause
        // the form to be submitted. This bypasses all the submission control/logic of reduxForm. As a workaround,
        // you can add an onSubmit event handler and use `event.preventDefault()` to disable this behavior.
        // See https://stackoverflow.com/questions/1370021/why-does-forms-with-single-input-field-submit-upon-pressing-enter-key-in-input
        return (
            <Step title={t('projectBuilder.people.groups.header')} subheader={t('projectBuilder.people.groups.subheader')}>
                <form className={classes.form} onSubmit={preventFormSubmission}>
                    {/* $FlowTypedIssue FieldArray validate type signature is missing name parameter */}
                    <FieldArray name="groups" component={this.renderGroups} validate={getValidationRule('groups')} />
                    <Typography className={classes.invitedCount} align="center" variant="caption">
                        {t('projectBuilder.people.groups.invitedCount', { count: memberCount })}
                    </Typography>
                </form>
            </Step>
        );
    }
}

const subscriptionSelector = entitySelector('subscriptions');
const mapStateToProps = (state: RootState, props: OwnProps): StateProps => {
    const { match } = props;
    return {
        editMode: subscriptionSelector(state, match.params.subscriptionId, 'state') === 'ACTIVE',
        initialValues: {
            ...getFormInitialValues('projectBuilder')(state),
            ...selectors.getInitialValues(state, props),
        },
        memberCount: selectors.getMemberCount(state),
    };
};

const connector: Connector<OwnProps, Props> = connect(mapStateToProps);
const enhance = compose(
    withStyles(styles, { name: 'Groups' }),
    withTranslation(),
    connector,
    reduxForm({
        form: 'projectBuilder',
        enableReinitialize: true,
        keepDirtyOnReinitialize: true,
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    })
);
export default enhance(Groups);
